/**
 * HandSampler — weighted-random selection from a Range with dead-card filtering.
 *
 * Per /design/specs/5_game_engine_spec.md §3:
 *   1. Filter range to combos that share no card with `deadCards`.
 *   2. Compute cumulative weight sum.
 *   3. Roll `rng.next() * totalWeight`; pick the combo at that cumulative position.
 *
 * If no combo survives filtering, throws (the engine treats this as a fatal
 * sampling error; SessionManager surfaces it as an infeasible session config).
 */

import { cardsEqual, type Card } from '@/types/card';
import type { Combo, Range, WeightedCombo } from '@/types/range';
import type { RNG } from '@/lib/rng';

/**
 * Sample a combo from `range`, skipping any combo that overlaps `deadCards`.
 *
 * Hero and villain sampling use the same algorithm; the only difference is
 * what `deadCards` contains (hero: board only; villain: board + hero's combo).
 */
export function sampleFromRange(
  range: Range,
  deadCards: readonly Card[],
  rng: RNG,
): Combo {
  const live = filterByDeadCards(range, deadCards);
  if (live.length === 0) {
    throw new Error('HandSampler: range exhausted by dead cards');
  }

  const total = live.reduce((sum, wc) => sum + wc.weight, 0);
  if (total <= 0) {
    throw new Error('HandSampler: total weight is zero after filtering');
  }

  const roll = rng.next() * total;
  let cumulative = 0;
  for (const wc of live) {
    cumulative += wc.weight;
    if (roll < cumulative) return wc.combo;
  }
  // Fallback for float-precision edge case (roll == total).
  return live[live.length - 1]!.combo;
}

/** Sample a hero combo. `deadCards` should be empty (or board cards if pre-sampled). */
export function sampleHero(
  range: Range,
  deadCards: readonly Card[],
  rng: RNG,
): Combo {
  return sampleFromRange(range, deadCards, rng);
}

/** Sample a villain combo. `deadCards` must include hero's combo. */
export function sampleVillain(
  range: Range,
  deadCards: readonly Card[],
  rng: RNG,
): Combo {
  return sampleFromRange(range, deadCards, rng);
}

/** Filter `range` to combos that share no card with `deadCards`. */
function filterByDeadCards(range: Range, deadCards: readonly Card[]): Range {
  if (deadCards.length === 0) return range;
  return range.filter(
    (wc) =>
      !deadCards.some(
        (d) => cardsEqual(d, wc.combo[0]) || cardsEqual(d, wc.combo[1]),
      ),
  );
}

/** Total weight of a range (sum of all WeightedCombo weights). */
export function rangeWeight(range: Range): number {
  return range.reduce((sum: number, wc: WeightedCombo) => sum + wc.weight, 0);
}
